import {
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  type LocalDataColumn,
  Skeleton,
  TOUCH,
} from "@nymbl/nos2-rules";
import { useState } from "react";
import { ReceiptPreview } from "@/components/expenses/ReceiptPreview";
import { RejectDialog } from "@/components/expenses/RejectDialog";
import { ApprovalChip, PaymentChip } from "@/components/expenses/StatusChips";
import {
  useApproveClaim,
  useDeleteClaim,
  useExpenseClaim,
  useSubmitClaim,
} from "@/hooks/use-expenses";
import { formatClaimDate, formatMoney } from "@/lib/expenses/view";
import type { ExpenseDetail } from "@/schemas/expenses";

const LINE_COLUMNS: LocalDataColumn<ExpenseDetail["lines"][number]>[] = [
  {
    key: "date",
    label: "Date",
    type: "date",
    accessor: (l) => l.date,
    render: (l) => (l.date ? formatClaimDate(l.date) : ""),
  },
  { key: "description", label: "Description", type: "text", accessor: (l) => l.description },
  {
    key: "amount",
    label: "Amount",
    type: "number",
    accessor: (l) => l.amount,
    render: (l) => formatMoney(l.amount),
  },
];

interface ClaimDetailDialogProps {
  id: string;
  onClose: () => void;
  onEdit: (claim: ExpenseDetail) => void;
}

function Fact({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

function Body({
  claim,
  onClose,
  onEdit,
}: {
  claim: ExpenseDetail;
  onClose: () => void;
  onEdit: (claim: ExpenseDetail) => void;
}) {
  const [confirm, setConfirm] = useState<"delete" | "approve" | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const submit = useSubmitClaim();
  const del = useDeleteClaim();
  const approve = useApproveClaim();
  return (
    <>
      <DialogBody className="flex flex-col gap-4">
        {claim.rejectionNote && (
          <div role="status" className="rounded-lg border border-danger bg-danger-bg px-4 py-2">
            <div className="text-sm font-semibold text-danger">Rejected</div>
            <div className="text-sm whitespace-pre-wrap">{claim.rejectionNote}</div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <ApprovalChip status={claim.approval} noApprover={claim.noApprover} />
          <PaymentChip status={claim.payment} approval={claim.approval} />
          <span className="ml-auto font-display text-2xl font-bold tabular-nums">
            {formatMoney(claim.total)}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {!claim.isMine && <Fact label="Person" value={claim.claimantName ?? ""} />}
          <Fact label="Type" value={claim.typeName} />
          <Fact label="Month" value={claim.monthName} />
          <Fact label="Department" value={claim.department ?? ""} />
          <Fact label="Purchase order" value={claim.po ?? ""} />
          <Fact
            label="Submitted"
            value={claim.dateSubmitted ? formatClaimDate(claim.dateSubmitted) : ""}
          />
          <Fact
            label="Approved"
            value={claim.approvedOn ? formatClaimDate(claim.approvedOn) : ""}
          />
          <Fact
            label="Est. payment"
            value={claim.estPaidOn ? formatClaimDate(claim.estPaidOn) : ""}
          />
        </dl>

        {claim.lines.length === 0 && <p className="text-sm text-muted-foreground">No lines</p>}
        {claim.lines.length > 0 && (
          <section aria-label="Lines">
            <DataTable
              label="Expense lines"
              exportName="expense-lines"
              columns={LINE_COLUMNS}
              rows={claim.lines}
              rowKey={(l) => l.id}
            />
          </section>
        )}

        {claim.receipts.length > 0 && (
          <section aria-label="Receipts" className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Receipts</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {claim.receipts.map((r) => (
                <ReceiptPreview key={r.id} receipt={r} />
              ))}
            </div>
          </section>
        )}

        {claim.notes.length > 0 && (
          <section aria-label="Notes" className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Notes</h3>
            <ul className="flex flex-col gap-2">
              {claim.notes.map((n) => (
                <li key={n.id} className="rounded-lg bg-muted px-3 py-2 text-sm">
                  <div className="whitespace-pre-wrap">{n.note}</div>
                  <div className="text-xs text-muted-foreground">
                    {[n.addedBy, n.addedOn ? formatClaimDate(n.addedOn.slice(0, 10)) : null]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </DialogBody>

      {(claim.canEdit || claim.canDecide) && (
        <DialogFooter>
          {claim.canEdit && (
            <>
              <Button
                type="button"
                variant="outline"
                className={`${TOUCH} text-danger`}
                onClick={() => setConfirm("delete")}
              >
                Delete
              </Button>
              <Button
                type="button"
                variant="outline"
                className={TOUCH}
                onClick={() => onEdit(claim)}
              >
                Edit
              </Button>
              <Button
                type="button"
                className={TOUCH}
                disabled={submit.isPending}
                onClick={() => submit.mutate({ id: claim.id }, { onSuccess: onClose })}
              >
                {claim.approval === "rejected" ? "Resubmit" : "Submit"}
              </Button>
            </>
          )}
          {claim.canDecide && (
            <>
              <Button
                type="button"
                variant="outline"
                className={`${TOUCH} text-danger`}
                onClick={() => setRejecting(true)}
              >
                Reject
              </Button>
              <Button type="button" className={TOUCH} onClick={() => setConfirm("approve")}>
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      )}

      <ConfirmDialog
        open={confirm === "delete"}
        onOpenChange={(open) => !open && !del.isPending && setConfirm(null)}
        title="Delete this expense?"
        description={claim.description}
        confirmLabel="Delete"
        cancelLabel="Keep"
        variant="destructive"
        loading={del.isPending}
        onConfirm={() => del.mutate({ id: claim.id }, { onSuccess: onClose })}
      />
      <ConfirmDialog
        open={confirm === "approve"}
        onOpenChange={(open) => !open && !approve.isPending && setConfirm(null)}
        title={`Approve ${formatMoney(claim.total)}?`}
        description={`${claim.claimantName ?? ""}: ${claim.description}`}
        confirmLabel="Approve"
        cancelLabel="Not yet"
        loading={approve.isPending}
        onConfirm={() => approve.mutate({ id: claim.id }, { onSuccess: onClose })}
      />
      {rejecting && (
        <RejectDialog claim={claim} onClose={() => setRejecting(false)} onDone={onClose} />
      )}
    </>
  );
}

/** Lines, receipts inline, notes and status of one claim (spec 3). */
export function ClaimDetailDialog({ id, onClose, onEdit }: ClaimDetailDialogProps) {
  const query = useExpenseClaim(id);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{query.data?.description ?? "Expense"}</DialogTitle>
          <DialogDescription className="sr-only">
            The expense's lines, receipts, notes and status.
          </DialogDescription>
        </DialogHeader>
        {query.isPending && (
          <DialogBody>
            <Skeleton className="h-64 w-full" />
          </DialogBody>
        )}
        {query.isError && (
          <DialogBody>
            <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
          </DialogBody>
        )}
        {query.data && <Body claim={query.data} onClose={onClose} onEdit={onEdit} />}
      </DialogContent>
    </Dialog>
  );
}
